import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, api } from '../api/client';
import { AuthBrand } from '../components/AuthBrand';
import { AuthField, AuthFieldSlot, AuthPasswordField } from '../components/AuthField';
import { PasswordValidationRules } from '../components/PasswordValidationRules';
import { AuthSplitCopy } from '../components/AuthSplitCopy';
import { AuthSplitDecor } from '../components/AuthSplitDecor';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { SelectMenu } from '../components/SelectMenu';
import { ThemeToggle } from '../components/ThemeToggle';
import { useLanguage } from '../lib/i18n/LanguageContext';
import {
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRIES,
  formatNationalDisplay,
  normalizeNationalInput,
  toE164,
} from '../lib/phoneCountries';
import { MIN_PASSWORD_LENGTH, isStrongPassword, normalizePasswordInput } from '../lib/password';

/**
 * Lands from the link emailed by handleAddMember (llmr-member-api).
 *
 * Two UI modes from GET /team/invite/validate:
 *   - account_exists=false: create-account form (new invitee)
 *   - account_exists=true: one-click accept (existing account / re-invite)
 *
 * On success this establishes a session (api.acceptInvite), so the caller
 * lands straight in the app via a full page load that re-reads the cookie.
 */
export function AcceptInvite() {
  const { dict } = useLanguage();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [displayName, setDisplayName] = useState('');
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY.iso2);
  const [phoneNational, setPhoneNational] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);
  const [accountExists, setAccountExists] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<'checking' | 'valid' | 'invalid'>(
    token ? 'checking' : 'invalid',
  );

  const phone = toE164(phoneCountry, phoneNational);
  const phoneLooksInvalid = phoneNational !== '' && phone === null;
  const passwordsMatch = confirmPassword === '' || confirmPassword === password;
  const passwordStrong = isStrongPassword(password);
  const passwordInvalid = password !== '' && !passwordStrong;
  const canSubmit =
    !busy && Boolean(phone) && passwordStrong && password === confirmPassword && confirmPassword !== '';

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api
      .validateInvite(token)
      .then((res) => {
        if (!cancelled) {
          setAccountExists(res.account_exists);
          setTokenStatus('valid');
        }
      })
      .catch(() => {
        if (!cancelled) setTokenStatus('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submitExisting(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await api.acceptInviteExisting(token);
      window.location.assign('/dashboard');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(dict.acceptInvite.errTooMany);
      } else if (err instanceof ApiError && err.is('/problems/conflict')) {
        setError(dict.acceptInvite.errAlreadyMember);
      } else if (err instanceof ApiError && err.is('/problems/invalid-or-expired-token')) {
        setInvalidToken(true);
      } else {
        setError(dict.acceptInvite.errGeneric);
      }
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!token || !phone) {
      if (!phone) setError(dict.acceptInvite.errInvalidPhone);
      return;
    }
    const normalizedPassword = normalizePasswordInput(password);
    const normalizedConfirm = normalizePasswordInput(confirmPassword);
    if (normalizedPassword !== password) setPassword(normalizedPassword);
    if (normalizedConfirm !== confirmPassword) setConfirmPassword(normalizedConfirm);
    if (normalizedPassword !== normalizedConfirm) {
      setError(dict.acceptInvite.errPasswordMismatch);
      return;
    }
    if (!isStrongPassword(normalizedPassword)) {
      setError(dict.acceptInvite.errWeakPassword);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.acceptInvite(token, normalizedPassword, phone, displayName.trim());
      window.location.assign('/dashboard');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(dict.acceptInvite.errTooMany);
      } else if (err instanceof ApiError && err.is('/problems/weak-password')) {
        setError(dict.acceptInvite.errWeakPassword);
      } else if (err instanceof ApiError && err.is('/problems/invalid-phone')) {
        setError(dict.acceptInvite.errInvalidPhone);
      } else if (err instanceof ApiError && err.is('/problems/phone-taken')) {
        setError(dict.acceptInvite.errPhoneTaken);
      } else if (err instanceof ApiError && err.is('/problems/conflict')) {
        setError(dict.acceptInvite.errAccountExists);
      } else if (err instanceof ApiError && err.is('/problems/invalid-or-expired-token')) {
        setInvalidToken(true);
      } else {
        setError(dict.acceptInvite.errGeneric);
      }
    } finally {
      setBusy(false);
    }
  }

  const missingToken = !token;
  const showInvalid = invalidToken || tokenStatus === 'invalid';

  return (
    <div className="auth-screen auth-split">
      <div className="auth-theme-toggle">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>

      <AuthBrand />

      <div className="auth-split-panel auth-split-panel--signup">
        <AuthSplitDecor />
        <AuthSplitCopy
          eyebrow={dict.acceptInvite.welcomeEyebrow}
          headline={dict.acceptInvite.welcomeHeadline}
          body={dict.acceptInvite.welcomeBody}
        />

        <div className="auth-split-form">
          <div className="auth-split-form-inner">
            {missingToken || showInvalid ? (
              <>
                <h1>
                  {missingToken ? dict.acceptInvite.missingTokenTitle : dict.acceptInvite.invalidTokenTitle}
                </h1>
                <p className="auth-lede">
                  {missingToken ? dict.acceptInvite.missingTokenBody : dict.acceptInvite.invalidTokenBody}
                </p>
                <Link to="/login" className="btn-block">
                  {dict.acceptInvite.goToSignIn}
                </Link>
              </>
            ) : tokenStatus === 'checking' ? (
              <p className="auth-lede">{dict.acceptInvite.checkingToken}</p>
            ) : accountExists ? (
              <form onSubmit={submitExisting}>
                <h1>{dict.acceptInvite.title}</h1>
                <p className="auth-lede">{dict.acceptInvite.ledeExisting}</p>
                {error && <div className="err">{error}</div>}
                <button type="submit" disabled={busy} className="btn-block">
                  {busy ? dict.acceptInvite.accepting : dict.acceptInvite.acceptJoin}
                </button>
              </form>
            ) : (
              <form onSubmit={submit}>
                <h1>{dict.acceptInvite.title}</h1>
                <p className="auth-lede">{dict.acceptInvite.lede}</p>
                {error && <div className="err">{error}</div>}

                <AuthField
                  id="displayName"
                  label={dict.acceptInvite.displayNameLabel}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  autoComplete="name"
                  autoFocus
                />

                <AuthFieldSlot
                  id="invite-phone-label"
                  label={dict.acceptInvite.phoneLabel}
                  className="auth-field--phone"
                  message={
                    phoneLooksInvalid ? <p className="note note-warn">{dict.acceptInvite.errInvalidPhone}</p> : null
                  }
                >
                  <div className="phone-row">
                    <SelectMenu
                      id="countryCode"
                      aria-label={dict.acceptInvite.countryCodeLabel}
                      value={phoneCountry}
                      className="phone-country-select"
                      onChange={(next) => {
                        setPhoneCountry(next);
                        setPhoneNational((prev) => normalizeNationalInput(next, prev));
                      }}
                      options={PHONE_COUNTRIES.map((c) => ({ value: c.iso2, label: c.label }))}
                    />
                    <input
                      id="phone"
                      type="tel"
                      inputMode="numeric"
                      value={formatNationalDisplay(phoneCountry, phoneNational)}
                      onChange={(e) =>
                        setPhoneNational(normalizeNationalInput(phoneCountry, e.target.value))
                      }
                      autoComplete="tel-national"
                      className="auth-field-input phone-national-input"
                      aria-labelledby="invite-phone-label"
                      aria-invalid={phoneLooksInvalid}
                    />
                  </div>
                </AuthFieldSlot>

                <AuthPasswordField
                  id="password"
                  label={dict.acceptInvite.passwordLabel}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD_LENGTH}
                  invalid={passwordInvalid}
                  aria-describedby={password !== '' ? 'invite-password-rules' : undefined}
                  showPassword={showPassword}
                  onToggleShow={() => setShowPassword(!showPassword)}
                  showLabel={dict.acceptInvite.showPassword}
                  hideLabel={dict.acceptInvite.hidePassword}
                  message={<PasswordValidationRules password={password} id="invite-password-rules" />}
                />

                <AuthPasswordField
                  id="confirmPassword"
                  label={dict.acceptInvite.confirmPasswordLabel}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  invalid={!passwordsMatch}
                  showPassword={showConfirmPassword}
                  onToggleShow={() => setShowConfirmPassword(!showConfirmPassword)}
                  showLabel={dict.acceptInvite.showPassword}
                  hideLabel={dict.acceptInvite.hidePassword}
                  message={
                    !passwordsMatch ? <p className="note note-warn">{dict.acceptInvite.errPasswordMismatch}</p> : null
                  }
                />

                <button type="submit" disabled={!canSubmit} className="btn-block">
                  {busy ? dict.acceptInvite.creatingAccount : dict.acceptInvite.createAccount}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
